import { Component, computed, DestroyRef, inject, OnInit, Signal, signal } from '@angular/core'
import { PageHeaderComponent } from '../public/page-header/page-header.component'
import {
  defaultEntityTypeCode,
  Entity,
  EntityRelationshipType,
  EntityTypeCode,
  entityTypeCodes,
  EntityTypeMap,
  EntityTypeMetadata,
  RelationshipType,
} from './entity-metadata'
import { EntityManagementToolbarComponent } from './entity-management-toolbar.component'
import { ActivatedRoute } from '@angular/router'
import { EntityListComponent } from './entity-list.component'
import { NewEntityComponent } from './new-entity.component'
import { EntityEditorComponent, RelationshipSelection } from './entity-editor.component'
import { ToastService } from '../public/toasts/toasts.service'
import { EntityManagementService } from './entity-management.service'
import { map, Observable, zip } from 'rxjs'
import { RouteDef } from '../routes'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { ChangeListComponent } from './change-list.component'
import { ChangeSet, EntityManagementChangesService } from './entity-management-changes.service'

type RouteParams = Readonly<{
  type: string
  entity: string
  relationshipType?: string
  relatedEntityType?: string
}>

const isRelationshipType = (raw: string | undefined): raw is RelationshipType =>
  raw === 'parent' || raw === 'child'

const isEntityTypeCode = (raw: string | undefined): raw is EntityTypeCode =>
  entityTypeCodes.includes(raw as EntityTypeCode)

const parseRelationshipSelection = ({ entity, relationshipType, relatedEntityType }: RouteParams): RelationshipSelection | undefined => {
  if (entity !== null && isRelationshipType(relationshipType) && isEntityTypeCode(relatedEntityType)) {
    return { relationshipType, relatedEntityType }
  } else {
    return undefined
  }
}

@Component({
  selector: 'app-entity-mgmt',
  imports: [
    PageHeaderComponent,
    EntityManagementToolbarComponent,
    EntityListComponent,
    NewEntityComponent,
    EntityEditorComponent,
    ChangeListComponent,
  ],
  template: `
    <div app-page-header [header]="routeDef()?.title ?? ''"></div>
    <div class='content-root'>
      <div class='content-left'>
        <div
            app-entity-mgmt-toolbar
            [entityTypeMap]="entityTypeMap()"
            [entityType]="entityType()"
            [path]="path()"
            (entityTypeChange)="onTypeChange($event)"
            (createNew)="setPath('new')"
        >
        </div>
        <app-entity-list
            [entities]="entities()"
            [selectedEntity]="selectedEntity()"
            [entityTypeMetadata]="entityTypeMetadata()"
            (selectionChange)="setPath($event)"
        />
        @if (changeSets().length > 0) {
          <app-change-list [rows]="changeSets()"/>
        }
      </div>
      <div class='content-right'>
        @switch (path()) {
          @case ('all') {
            <div class="alert alert-info" role="alert">
              Select an item in the list to view details
            </div>
          }
          @case ('new') {
            <app-new-entity
                [entities]="entities()"
                [entityTypeMetadata]="entityTypeMetadata()"
                (cancel)="setPath('all')"
                (saveComplete)="onNewEntityCreated($event)"
                (error)="onError($event)"
            />
          }
          @default {
            <app-entity-editor
                [entity]="selectedEntity()"
                [url]="url()"
                [tabSelection]="relationshipSelection()"
                [entityTypeMap]="entityTypeMap()"
                [entityRelationshipTypes]="entityRelationshipTypes()"
                (tabSelectionChange)="onTabSelectionChange($event)"
                (saveComplete)="refreshEntities()"
                (error)="onError($event)"
            />
          }
        }
      </div>
    </div>
  `,
  styles: `
    .content-root {
      display: flex;
      padding: 0 3rem 0 3rem;
      
      .content-left, .content-right {
        height: calc(100vh - 170px);
        display: flex;
        flex-direction: column;
        flex-basis: 0;
      }
      .content-left {
        flex-grow: 2;
        app-entity-list {
          flex-basis: 0;
          flex-grow: 2;
          width: 100%;
        }
        app-change-list {
          flex-basis: 0;
          flex-grow: 1;
          width: 100%;
        }
      }
      .content-right {
        padding-left: 1rem;
        min-width: 0;
        flex-grow: 3;
        .alert {
          margin-top: 0.5rem;
        }
      }
    }
  `,
})
export class EntityManagementComponent implements OnInit {
  private readonly entityManagementService = inject(EntityManagementService)
  private readonly changesService = inject(EntityManagementChangesService)
  private readonly toastService = inject(ToastService)
  private readonly route = inject(ActivatedRoute)
  private readonly destroyRef = inject(DestroyRef)

  protected readonly entityTypeMap = signal<EntityTypeMap | undefined>(undefined)
  protected readonly entityType = signal<EntityTypeCode | undefined>(undefined)
  protected readonly entityRelationshipTypes = signal<readonly EntityRelationshipType[]>([])
  protected readonly entities = signal<readonly Entity[]>([])
  protected readonly path = signal('')
  protected readonly relationshipSelection = signal<RelationshipSelection | undefined>(undefined)

  protected readonly routeDef: Signal<RouteDef | undefined> = toSignal(
    this.route.data.pipe(map(data => data['routeDef'] as RouteDef)),
  )

  protected readonly changeSets = computed((): ChangeSet[] =>
    this.changesService.changeSets().filter(_ => _.changes.length > 0),
  )

  protected readonly url = computed((): string => {
    const routeDef = this.routeDef()
    if (routeDef) {
      const baseUrl = `/${routeDef.root}/${routeDef.relativePath}/${this.entityType()}/${this.path()}`
      const relationshipSelection = this.relationshipSelection()
      return relationshipSelection ? `${baseUrl}/${relationshipSelection.relationshipType}/${relationshipSelection.relatedEntityType}` : baseUrl
    } else {
      return '#'
    }
  })

  protected readonly selectedEntityId = computed((): string | undefined => {
    const path = this.path()
    return path.length === 0 || path === 'all' || path === 'new' ? undefined : path
  })

  protected readonly selectedEntity = computed((): Entity | undefined => {
    const entityUid = this.selectedEntityId()
    return entityUid ? this.entities().find(_ => _.entity_uid === entityUid) : undefined
  })

  protected readonly entityTypeMetadata = computed((): EntityTypeMetadata | undefined => {
    const entityTypeMap = this.entityTypeMap()
    const entityType = this.entityType()
    if (entityTypeMap && entityType) {
      return entityTypeMap[entityType]
    } else {
      return undefined
    }
  })

  protected onError = (error: unknown) => {
    this.toastService.showError(error)
  }

  constructor() {
    this.route.params.subscribe(rawParams => {
      const params = rawParams as RouteParams
      this.path.set(params.entity)
      if (params.type !== this.entityType()) {
        this.initEntityType(params.type)
      }
      this.relationshipSelection.set(parseRelationshipSelection(params))
    })
  }

  private initEntityType(type: string) {
    if (entityTypeCodes.includes(type as EntityTypeCode)) {
      this.entityType.set(type as EntityTypeCode)
      this.maybeGetEntities()
    } else {
      // the :type in URL is not valid... switch to the default
      this.onTypeChange(defaultEntityTypeCode)
    }
  }

  ngOnInit() {
    this.getEntityMetadata().subscribe({
      next: ([entityTypeMap, relationshipTypes]) => {
        this.entityTypeMap.set(entityTypeMap)
        this.entityRelationshipTypes.set(relationshipTypes)
        this.maybeGetEntities()
      },
      error: this.onError,
    })
  }

  private getEntityMetadata(): Observable<[EntityTypeMap, readonly EntityRelationshipType[]]> {
    return zip([
      this.entityManagementService.getEntityTypeMap(),
      this.entityManagementService.getRelationshipTypes(),
    ]).pipe(takeUntilDestroyed(this.destroyRef))
  }

  private maybeGetEntities() {
    if (this.entityTypeMap() && this.entityType()) {
      this.entities.set([])
      this.entityManagementService.getEntities(this.entityType()!)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: entities => {
            this.entities.set(entities)
            this.validatePath()
          },
          error: this.onError,
        })
    }
  }

  private validatePath() {
    const path = this.path()
    if (path !== 'new' && path !== 'all' && !this.selectedEntity()) {
      // path has an entity ID in it that does not exist or has been filtered
      // redirect to /all
      this.setPath('all')
    }
  }

  protected onTypeChange(type: EntityTypeCode) {
    this.entityType.set(type)
    this.path.set('all')
    this.relationshipSelection.set(undefined)
    this.maybeGetEntities()
    this.updateState()
  }

  protected setPath(path: string | undefined) {
    this.path.set(path ?? 'all')
    if (!path) {
      this.relationshipSelection.set(undefined)
    }
    this.updateState(true)
  }

  protected onTabSelectionChange(selection: RelationshipSelection | undefined) {
    this.relationshipSelection.set(selection)
    this.updateState()
  }

  private updateState(replace = false) {
    history[replace ? 'replaceState' : 'pushState']({}, '', this.url())
  }

  protected onNewEntityCreated(newEntityUid: string) {
    this.setPath(newEntityUid)
    this.refreshEntities()
  }

  protected refreshEntities() {
    this.maybeGetEntities()
  }
}
